<template>
    <v-dialog :value="value" max-width="500px" persistent @input="$emit('input', $event)">
        <v-card>
            <v-card-title>
                <span class="text-h6 font-weight-bold">Edit Leave Balance</span>
                <v-spacer></v-spacer>
                <v-btn icon :disabled="saving" @click="$emit('input', false)">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-6">
                <div v-if="user" class="mb-4">
                    <div class="d-flex align-center mb-4">
                        <v-avatar size="48" color="primary" class="mr-3">
                            <span class="white--text">{{ initialsOf(user) }}</span>
                        </v-avatar>
                        <div>
                            <div class="font-weight-bold">{{ displayName }}</div>
                            <div v-if="user.email" class="text-caption grey--text">{{ user.email }}</div>
                        </div>
                    </div>
                    <v-alert dense text type="info" class="mb-4">
                        Resulting balance for {{ yearSel }}: <strong>{{ resulting.toFixed(2) }} days</strong>
                    </v-alert>
                </div>

                <v-select
                    v-model="yearSel"
                    :items="availableYears"
                    label="Year"
                    outlined
                    dense
                    class="mb-2"
                    :rules="[v => !!v || 'Year is required']"
                ></v-select>

                <v-select
                    v-model="typeSel"
                    :items="requestTypes"
                    item-text="name"
                    item-value="id"
                    label="Request Type"
                    outlined
                    dense
                    :rules="[v => !!v || 'Leave type is required']"
                ></v-select>

                <v-text-field
                    v-model.number="credits"
                    label="Leave credits"
                    outlined
                    dense
                    type="number"
                    min="0"
                    step="0.5"
                    :hint="`Edit the entitlement for this leave type in ${yearSel}`"
                    persistent-hint
                    class="mb-2"
                    :rules="[v => v >= 0 || 'Leave credits cannot be negative']"
                ></v-text-field>

                <v-text-field
                    v-model.number="used"
                    label="Used leaves"
                    outlined
                    dense
                    type="number"
                    min="0"
                    step="0.5"
                    hint="Manually correct the number of leaves already used. Use with care - this does not change any individual leave request."
                    persistent-hint
                    :rules="[v => v >= 0 || 'Used leaves cannot be negative']"
                ></v-text-field>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn text :disabled="saving" @click="$emit('input', false)">Cancel</v-btn>
                <v-btn color="primary" :disabled="!canSave" :loading="saving" @click="save">
                    <v-icon left small>mdi-check</v-icon>
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { fullName, initials } from './leaveUtils';

// Same form as "Edit Balance" on /leave-management (year, leave type, credits, used
// leaves), shown from a person's calendar. Only Vacation (1) and Accumulated Vacation (6)
// carry an entitlement. The panel sends the request and refreshes afterwards.
const ENTITLEMENT_TYPE_IDS = [1, 6];

export default {
    name: 'LeaveAllowanceDialog',
    props: {
        value: { type: Boolean, default: false },
        user: { type: Object, default: null },
        year: { type: Number, required: true }, // year the calendar was showing
        leaveTypes: { type: Array, default: () => [] },
        // that person's rows: [{ leave_type_id, year, leave_count, used_leaves }]
        counts: { type: Array, default: () => [] },
        saving: { type: Boolean, default: false },
    },
    data() {
        return { yearSel: this.year, typeSel: null, credits: 0, used: 0 };
    },
    computed: {
        displayName() {
            return this.user ? fullName(this.user) : '';
        },
        requestTypes() {
            return this.leaveTypes.filter((t) => ENTITLEMENT_TYPE_IDS.includes(Number(t.id)));
        },
        // a small range around now, plus any year that has data for this person
        availableYears() {
            const years = new Set();
            const now = new Date().getFullYear();
            for (let y = now - 3; y <= now + 1; y++) years.add(y);
            years.add(this.year);
            this.counts.forEach((c) => years.add(Number(c.year)));
            return Array.from(years).sort((a, b) => b - a);
        },
        resulting() {
            return (Number(this.credits) || 0) - (Number(this.used) || 0);
        },
        canSave() {
            return !!this.typeSel && !!this.yearSel && Number(this.credits) >= 0 && Number(this.used) >= 0
                && this.credits !== '' && this.used !== '';
        },
    },
    watch: {
        value(open) {
            if (!open) return;
            this.yearSel = this.year;
            const existing = this.counts.find(
                (c) => Number(c.year) === this.yearSel && ENTITLEMENT_TYPE_IDS.includes(Number(c.leave_type_id))
            );
            this.typeSel = existing ? Number(existing.leave_type_id) : (this.requestTypes[0] && this.requestTypes[0].id) || null;
            this.sync();
        },
        yearSel() {
            this.sync();
        },
        typeSel() {
            this.sync();
        },
    },
    methods: {
        initialsOf(u) {
            return initials(fullName(u));
        },
        // fields follow the record for the chosen year + type (0 when none exists yet)
        sync() {
            if (!this.value) return;
            const rec = this.counts.find(
                (c) => Number(c.year) === Number(this.yearSel) && Number(c.leave_type_id) === Number(this.typeSel)
            );
            this.credits = rec ? Number(rec.leave_count) || 0 : 0;
            this.used = rec ? Number(rec.used_leaves) || 0 : 0;
        },
        save() {
            if (!this.canSave) return;
            this.$emit('save', {
                year: Number(this.yearSel),
                items: [{
                    leave_type_id: Number(this.typeSel),
                    leave_count: Number(this.credits),
                    used_leaves: Number(this.used),
                }],
            });
        },
    },
};
</script>
